import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Circle, Clock, PlayCircle, RotateCcw, X } from "lucide-react";
import { useTour } from "./TourContext";
import { TOUR_CATALOG } from "./tours";
import type { Tour } from "./types";

/**
 * "Getting started" onboarding hub — a modal catalog of every tour, with
 * per-tour completion state (driven by the same localStorage dismissal
 * flags the auto-start path uses) so it doubles as a progress checklist.
 *
 * Launching a tour from here always uses {@code force: true}, so it wins
 * over any page-mount auto-start and replays even after dismissal. For
 * cross-page flows the overlay drives router navigation from step 1.
 *
 * Opened from {@link TourLauncher}'s "Getting started" entry. Closing the
 * hub then launching a tour is a two-step interaction by design — the hub
 * stays a planning surface, the overlay is the doing surface.
 */
export function TourHub({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { start, isDismissed, resetAllDismissals, active } = useTour();
  const panelRef = useRef<HTMLDivElement>(null);

  // Esc closes; focus moves into the panel on open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const id = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(id);
    };
  }, [open, onClose]);

  // An active tour means the user just launched one — get out of the way.
  useEffect(() => {
    if (active && open) onClose();
  }, [active, open, onClose]);

  const groups = useMemo(() => groupByCategory(TOUR_CATALOG), []);
  const completed = TOUR_CATALOG.filter((t) => isDismissed(t.id)).length;

  if (!open) return null;

  const launch = (tour: Tour) => {
    start(tour, { force: true });
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[55] flex items-start justify-center p-4 pt-[8vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Getting started with Operate"
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]" onClick={onClose} />

      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl outline-none ring-1 ring-slate-900/5"
      >
        {/* Header. */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Getting started</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Guided tours of every Operate surface. {completed} of {TOUR_CATALOG.length} explored.
            </p>
            {/* Progress bar. */}
            <div className="mt-2 h-1.5 w-56 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${(completed / TOUR_CATALOG.length) * 100}%` }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Catalog. */}
        <div className="flex-1 overflow-auto px-5 py-4">
          {groups.map(([category, tours]) => (
            <div key={category} className="mb-5 last:mb-0">
              <div className="mb-2 text-2xs font-semibold uppercase tracking-wider text-slate-400">
                {categoryLabel(category)}
              </div>
              <ul className="space-y-1.5">
                {tours.map((tour) => {
                  const done = isDismissed(tour.id);
                  return (
                    <li key={tour.id}>
                      <button
                        type="button"
                        onClick={() => launch(tour)}
                        className="group flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left transition hover:border-emerald-300 hover:bg-emerald-50"
                      >
                        {done ? (
                          <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                        ) : (
                          <Circle className="h-5 w-5 flex-shrink-0 text-slate-300" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-slate-900">
                              {tour.title}
                            </span>
                            {tour.estMinutes != null && (
                              <span className="inline-flex flex-shrink-0 items-center gap-0.5 text-2xs text-slate-400">
                                <Clock className="h-3 w-3" />
                                {tour.estMinutes} min
                              </span>
                            )}
                          </div>
                          {tour.subtitle && (
                            <div className="truncate text-xs text-slate-500">{tour.subtitle}</div>
                          )}
                        </div>
                        <PlayCircle className="h-4 w-4 flex-shrink-0 text-slate-300 transition group-hover:text-emerald-600" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer. */}
        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={resetAllDismissals}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset progress
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Group the catalog by `category`, preserving first-seen order. */
function groupByCategory(tours: Tour[]): Array<[string, Tour[]]> {
  const map = new Map<string, Tour[]>();
  for (const t of tours) {
    const key = t.category ?? "page";
    const list = map.get(key) ?? [];
    list.push(t);
    map.set(key, list);
  }
  return Array.from(map.entries());
}

function categoryLabel(category: string): string {
  if (category === "flow") return "Guided flows";
  if (category === "page") return "Page tours";
  return category.charAt(0).toUpperCase() + category.slice(1);
}
